// Full one-to-one rewrite using React + Material UI
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  IconButton,
} from "@mui/material";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Add, Delete, Edit, Save, Close } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import config from "../../../config/config.json";
import { useAuth } from "../../../context/AuthContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import Loading from "../../loading";

export default function AdminFeesUI() {
  const { agentId: urlAgentId } = useParams();
  const { token, logout } = useAuth();
  const [dataSets, setDataSets] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [activeDataSet, setActiveDataSet] = useState("1");
  const [editingItem, setEditingItem] = useState(null);
  const [feeBreakdownItem, setFeeBreakdownItem] = useState(null);
  const [feeData, setFeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [defaultAgentId, setDefaultAgentId] = useState(null);
  const [editedBreakdownFees, setEditedBreakdownFees] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const location = useLocation();
  const [addBreakdownDialogOpen, setAddBreakdownDialogOpen] = useState(false);
  const [newBreakdownFee, setNewBreakdownFee] = useState({
    durationId: "",
    amount: "",
  });
  const [agentData, setAgentData] = useState(null);
  const [addSubcategoryItem, setAddSubcategoryItem] = useState(null);
  const [descriptionError, setDescriptionError] = useState("");
  const [subcategoryDescriptionError, setSubcategoryDescriptionError] = useState("");

  const isAgentDetailPage = location.pathname.startsWith("/admin/agent-data/");
  const navigate = useNavigate();

  const currentDataSet = dataSets.find((ds) => ds.id === activeDataSet);

  const handleChangeTab = (_, newValue) => setActiveTab(newValue);

  const handleAddFee = () => {
    const newCategoryId = uuidv4();
    setEditingItem({
      id: newCategoryId,
      description: "",
      duration: "",
      amount: 0,
      currency: "CAD",
      type: "add-on",
      newCategory: true,
    });
  };

  const handleAddSubcategory = (parentCategoryId) => {
    setAddSubcategoryItem({
      id: uuidv4(),
      description: "",
      FeeCategoryId: parentCategoryId,
    });
  };

  const handleSaveSubcategoryItem = async () => {
    let response;
    if(addSubcategoryItem.subCategoryId){
      //update the subcategory
      const endpoint = `${config.base_url}/api/admin/sub-fee-categories/${addSubcategoryItem.subCategoryId}`;
       response = await axios.patch(endpoint, {
        name: addSubcategoryItem.description,
        FeeCategoryId: addSubcategoryItem.FeeCategoryId,
      }, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });
    }else{
      //add the subcategory
      const endpoint = `${config.base_url}/api/admin/sub-fee-categories`;
       response = await axios.post(endpoint, {
        name: addSubcategoryItem.description,
        AgentId: urlAgentId || defaultAgentId,
        FeeCategoryId: addSubcategoryItem.FeeCategoryId,
      }, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });
    }
    if(response.data){
      console.log("subcategory response", response.data);
      setAddSubcategoryItem(null);
      window.location.reload();
    }
  };

  // Fetch default agent ID if no specific agent ID is provided
  useEffect(() => {
    const fetchDefaultAgent = async () => {
      if (urlAgentId) return; // Skip if we have a specific agent ID
      console.log("fetching default agent");
      try {
        const response = await axios.get(
          `${config.base_url}/api/admin/agents/default`,
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );
        console.log("default agent response", response.data);
        setDefaultAgentId(response.data.id);
      } catch (error) {
        if (error.response?.status === 401) {
          console.warn("401 Unauthorized - logging out...");
          logout(); // ✅ call logout from AuthContext
        } else {
          console.error("Error fetching default agent:", error);
          setError("Failed to load default agent. Please try again.");
        }
      }
    };

    fetchDefaultAgent();
  }, [token, urlAgentId]);

  // Helper function to check if agent has fee data
  const hasFeeData = (data) => {
    if (!data || !data.feesByCategory) return false;

    // Check if any category has fees
    return Object.values(data.feesByCategory).some(
      (category) => category.fees && category.fees.length > 0
    );
  };

  // Fetch fee data when we have either a specific agent ID or default agent ID
  useEffect(() => {
    fetchFeeData();
  }, [urlAgentId, defaultAgentId, token]);
  const fetchFeeData = async () => {
    const effectiveAgentId = urlAgentId || defaultAgentId;
    if (!effectiveAgentId) return; // Don't fetch if we don't have an ID

    try {
      setLoading(true);
      const endpoint = `${config.base_url}/api/fees/${effectiveAgentId}`;

      try {
        // Try to get the specific agent's fee data
        const response = await axios.get(endpoint, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });

        const apiData = response.data;

        // Check if the agent has any fee data
        if (!hasFeeData(apiData) && urlAgentId) {
          // If no fee data and this is a specific agent, fetch default data
          //fetch default agent id
          const defaultAgentId = await axios.get(
            `${config.base_url}/api/admin/agents/default`,
            {
              headers: {
                Authorization: token,
                "Content-Type": "application/json",
              },
            }
          );
          const defaultResponse = await axios.get(
            `${config.base_url}/api/fees/${defaultAgentId.data.id}`,
            {
              headers: {
                Authorization: token,
                "Content-Type": "application/json",
              },
            }
          );

          const defaultData = defaultResponse.data;
          const flatFees = defaultData.feeCategories.map((category, idx) => ({
            id: category.id || `category-${idx}`,
            description: category.name,
            currency: "CAD",
            type: category.type,
            subCategory: category.subCategoryId,
            hasSubCategory: category.hasSubCategory,
          }));

          setDataSets([
            {
              id: "1",
              name: "2025-2026 Academic Year",
              year: "2025-2026",
              assignedAgent: urlAgentId, // Use the specific agent ID
              mainFees: flatFees,
            },
          ]);

          setFeeData(defaultData);
        } else {
          // Use the agent's own data
          const flatFees = apiData.feeCategories.map((category, idx) => ({
            id: category.id || `category-${idx}`,
            description: category.name,
            currency: "CAD",
            type: category.type,
            subCategory: category.subCategoryId,
            hasSubCategory: category.hasSubCategory,
          }));
          //if some data has subCategory then map all the subcategories to that object to be shown on the table

          setDataSets([
            {
              id: "1",
              name: "2025-2026 Academic Year",
              year: "2025-2026",
              assignedAgent: effectiveAgentId,
              mainFees: flatFees,
              subCategoriesByParent: apiData.subCategoriesByParent,
            },
          ]);

          setFeeData(apiData);
        }
      } catch (error) {
        // If API call fails and we have a specific agent ID, fetch default data
        if (error.response?.status === 404 && urlAgentId) {
          const defaultResponse = await axios.get(
            `${config.base_url}/api/fees/${defaultAgentId}`,
            {
              headers: {
                Authorization: token,
                "Content-Type": "application/json",
              },
            }
          );

          const defaultData = defaultResponse.data;
          const flatFees = defaultData.feeCategories.map((category, idx) => ({
            id: category.id || `category-${idx}`,
            description: category.name,
            currency: "CAD",
            type: category.type,
            subCategory: category.subCategoryId,
            hasSubCategory: category.hasSubCategory,
          }));

          setDataSets([
            {
              id: "1",
              name: "2025-2026 Academic Year",
              year: "2025-2026",
              assignedAgent: urlAgentId, // Use the specific agent ID
              mainFees: flatFees,
            },
          ]);

          setFeeData(defaultData);
        } else {
          throw error;
        }
      }

      setError(null);
    } catch (error) {
      console.error("Error fetching fee data:", error);
      setError("Failed to load fee data. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleSaveItem = async () => {
    try {
      //if it is not new category then update the category using api  - router.patch('/fee-categories/:id', updateFeeCategory);
      if (!editingItem.newCategory) {
        const endpoint = `${config.base_url}/api/admin/fee-categories/${editingItem.id}`;
        const feeCategoryData = {
          type: editingItem.type,
          name: editingItem.description,
          currency: editingItem.currency,
          hasSubCategory: editingItem.hasSubCategory,
          id: editingItem.id,
        };
        const response = await axios.patch(endpoint, feeCategoryData, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });
        //reload screen
        window.location.reload();
        return;
      }
      if (!urlAgentId && !defaultAgentId) {
        throw new Error("No agent ID available");
      }

      // Prepare the fee category data
      const feeCategoryData = {
        name: editingItem.description,
        type: editingItem.type,
        appliesToDuration: 1, // Default to applies to duration
        isVariableByAgent: 1, // Default to variable by agent
        agentId: urlAgentId || defaultAgentId,
        hasSubCategory: editingItem.hasSubCategory,
      };

      const endpoint = `${config.base_url}/api/admin/fee-categories`;
      const response = await axios.post(endpoint, feeCategoryData, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      if (response.data) {
        // Update local state with the response data
        const newCategory = response.data;
        console.log("current data set",currentDataSet);
        const flatFees = [
          ...(currentDataSet?.mainFees || []),
          {
            id: newCategory.id,
            description: newCategory.name,
            currency: "CAD",
            type: newCategory.type,
            subCategory: newCategory.subCategoryId,
            hasSubCategory: newCategory.hasSubCategory,
          },
        ];

        setDataSets([
          {
            id: "1",
            name: "2025-2026 Academic Year",
            year: "2025-2026",
            assignedAgent: urlAgentId,
            mainFees: flatFees,
            subCategoriesByCategory: newCategory.subCategoriesByParent,
          },
        ]);

        setEditingItem(null);
        window.location.reload();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn("401 Unauthorized - logging out...");
        logout(); // ✅ logout on token expiration
      } else {
        console.error("Error saving item:", error);
        setError("Failed to save item. Please try again.");
      }
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      const effectiveAgentId = urlAgentId || defaultAgentId;
      if (!effectiveAgentId) {
        throw new Error("No agent ID available");
      }

      const endpoint = `${config.base_url}/api/admin/fee-categories/${id}`;
      await axios.delete(endpoint, {
        data: {
          AgentId: effectiveAgentId,
        },
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });      

      setDataSets((prev) =>
        prev.map((ds) =>
          ds.id === activeDataSet
            ? { ...ds, mainFees: ds.mainFees.filter((item) => item.id !== id) }
            : ds
        )
      );
      //reload the page
      window.location.reload();
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn("401 Unauthorized - logging out...");
        logout(); // ✅ Logout on token expiration
      } else {
        console.error("Error deleting item:", error);
        setError("Failed to delete item. Please try again.");
      }
    }
  };

  const handleEditItem = (item, newCategory) =>
    setEditingItem({ ...item, newCategory });

  const handleViewBreakdown = (categoryName, subCategoryName = null) => {
    const categoryData = feeData?.feesByCategory?.[categoryName];
    if (!categoryData) return;

    if (subCategoryName) {
      const filteredFees = categoryData.fees.filter(
        (fee) => fee.subCategoryName === subCategoryName
      );

      if (filteredFees.length > 0) {
        setFeeBreakdownItem({
          fees: filteredFees,
          category: subCategoryName,
          categoryId: categoryData.id,
          isSubCategory: true,
        });
      }
    } else {
      setFeeBreakdownItem({
        ...categoryData,
        category: categoryName,
        categoryId: categoryData.id,
        isSubCategory: false,
      });
    }
  };

  const handleEditFee = (fee) => {
    setEditingItem(fee);
  };

  const handleEditSubItem=(sub,newSubCategory)=>{
    //show sub category dialog
    console.log("sub",sub);
    setAddSubcategoryItem({
      subCategoryId: sub.id,
      description: sub.name,
      FeeCategoryId: sub.FeeCategoryId,
    } );
    
  }

  // When opening breakdown dialog, initialize editedBreakdownFees
  useEffect(() => {
    if (feeBreakdownItem) {
      setEditedBreakdownFees(
        (feeBreakdownItem.fees || []).map((fee) => ({ ...fee }))
      );
    }
  }, [feeBreakdownItem]);

  // Handler for editing a fee value inline
  const handleEditBreakdownFee = (index, value) => {
    setEditedBreakdownFees((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], amount: value };
      return updated;
    });
  };

  // Restore default fees
  const handleRestoreDefaultBreakdownFees = () => {
    setEditedBreakdownFees(
      (feeBreakdownItem.fees || []).map((fee) => ({ ...fee }))
    );
  };



  const handleSaveNewBreakdownFee = () => {
    if (!newBreakdownFee.durationId || newBreakdownFee.amount === "") return;
    const durationObj = feeData.durations.find(
      (d) => d.id === newBreakdownFee.durationId
    );
    setEditedBreakdownFees((prev) => [
      ...prev,
      {
        durationId: newBreakdownFee.durationId,
        durationName: durationObj?.name || "",
        amount: Number(newBreakdownFee.amount),
        // Add other required fields if needed
      },
    ]);
    setAddBreakdownDialogOpen(false);
  };

  const handleCancelNewBreakdownFee = () => {
    setAddBreakdownDialogOpen(false);
  };

  // Save handler (stub for now)
  const handleSaveBreakdownFees = async () => {
    try {
      await axios.post(
        `${config.base_url}/api/admin/fee-values/bulk-update`,
        {
          feeValues: editedBreakdownFees.map((fee) => ({
            ...fee,
            AgentId: urlAgentId || defaultAgentId,
            FeeCategoryId: feeBreakdownItem.categoryId,
          })),
        },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );
      setFeeBreakdownItem(null);
      //recall the api to fetch the fee data
      fetchFeeData();
    } catch (err) {
      if (err.response?.status === 401) {
        console.warn("401 Unauthorized - logging out...");
        logout(); // ✅ Auto logout on 401
      } else {
        console.error("Failed to update fees", err);
        
      }
    }
  };

  // Cancel handler
  const handleCancelBreakdownFees = () => {
    setFeeBreakdownItem(null);
  };

  // Fetch agent data on page load if urlAgentId is present
  useEffect(() => {
    const fetchAgentData = async () => {
      if (!urlAgentId) return;
      try {
        const response = await axios.get(
          `${config.base_url}/api/admin/agents/${urlAgentId}`,
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );
        setAgentData(response.data);
      } catch (err) {
        if (err.response?.status === 401) {
          console.warn("401 Unauthorized - logging out...");
          logout(); // ✅ Call logout if unauthorized
        } else {
          console.error("Failed to fetch agent data", err);
          setAgentData(null);
        }
      }
    };
    fetchAgentData();
  }, [urlAgentId, token]);

  if (loading) {
    return (
      <Loading />
      // <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
      //   <Typography>Loading...</Typography>
      // </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
console.log(editingItem,"editing item---")
  return (
    <Box p={2}>
      {/* Conditionally show Back Button */}
      <div style={{ display: "flex", gap: "2px" }}>
        {urlAgentId && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin/manage-agent")}
            sx={{ mb: 1, padding: "0px" }}
            className="back-btn"
          ></Button>
        )}
        <Typography variant="h5" gutterBottom>
          {urlAgentId
            ? `${agentData?.name || "Agent"} Fee Management`
            : "Default Fee Management"}
        </Typography>
      </div>

      {/* Conditionally show Breadcrumbs */}
      {urlAgentId && (
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/admin/manage-agent")}
          >
            Home
          </Link>
          <Typography color="var(--primary)">Agent Fee Management</Typography>
        </Breadcrumbs>
      )}

      <Box mt={2}>
        <Card>
          <CardHeader
            title="Fees List"
            className="fee-card-header"
            action={
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddFee}
                className="dashboard-btns"
              >
                Add Fee
              </Button>
            }
          />
          <CardContent
            sx={{
              height: isAgentDetailPage ? "60vh" : "53vh",
              overflowY: "scroll",
            }}
          >
            <Box>
              {currentDataSet?.mainFees.map((fee) => {
                const subCategories =
                  fee.subCategory &&
                  feeData?.subCategoriesByParent?.[fee.description];

                return (
                  <Accordion key={fee.id} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      
                      <Box
                        display="flex"
                        alignItems="center"
                         gap="12px"
                        width="100%"
                      >
                        <Typography>{fee.description}</Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent accordion toggle
                            handleEditItem(fee, false); 
                          }}
                        >
                          <Edit
                            fontSize="small"
                            sx={{ color: "var(--secondary)" }}
                          />
                        </IconButton>
                        {
                          fee.hasSubCategory ? (
                            <>
                            <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAgentToDelete(fee.id);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <Delete
                              fontSize="small"
                              sx={{ color: "var(--secondary)" }}
                            />
                          </IconButton>
                          
                           <Button
                           variant="contained"
                           startIcon={<Add />}
                           
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent accordion toggle
                            handleAddSubcategory(fee.id)}}
                           className="dashboard-btns subcategory-btn"
                           style={{ marginLeft:"auto"}}
                         >
                           Add Subcategory  
                         </Button></>
                           ): null
                        }
                      </Box>
                    </AccordionSummary>
                    
                   
                    <AccordionDetails>
                      {subCategories?.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Subcategory</TableCell>
                              <TableCell>Currency</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {subCategories.map((sub) => (
                              <TableRow key={sub.id}>
                                <TableCell>{sub.name}</TableCell>
                                <TableCell>{fee.currency}</TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    onClick={() => handleEditSubItem(sub, false)}
                                  >
                                    <Edit
                                      fontSize="small"
                                      sx={{ color: "var(--secondary)" }}
                                    />
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setAgentToDelete(sub.id);
                                      setDeleteConfirmOpen(true);
                                    }}
                                  >
                                    <Delete
                                      fontSize="small"
                                      sx={{ color: "var(--secondary)" }}
                                    />
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    onClick={() =>
                                      handleViewBreakdown(
                                        fee.description,
                                        sub.name
                                      )
                                    }
                                    sx={{
                                      color: "var(--secondary)",
                                      borderColor: "var(--secondary)",
                                    }}
                                  >
                                    Edit Fees
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography>Currency: {fee.currency}</Typography>
                          <Box>
                            {/* <Button
                              size="small"
                              onClick={() => handleEditItem(fee, false)}
                            >
                              <Edit
                                fontSize="small"
                                sx={{ color: "var(--secondary)" }}
                              />
                            </Button> */}
                            {!fee.hasSubCategory && (
                              <>
                            <Button
                              size="small"
                              onClick={() => {
                                setAgentToDelete(fee.id);
                                setDeleteConfirmOpen(true);
                              }}
                            >
                              <Delete
                                fontSize="small"
                                sx={{ color: "var(--secondary)" }}
                              />
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() =>
                                handleViewBreakdown(fee.description)
                              }
                              sx={{
                                color: "var(--secondary)",
                                borderColor: "var(--secondary)",
                              }}
                            >
                              Edit Fees
                            </Button>
                            </>
                            )}
                          </Box>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Dialog for editing */}
      <Dialog
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {editingItem?.newCategory ? "Add Fee" : "Edit Fee"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={editingItem?.description || ""}
            onChange={(e) => {
              const value = e.target.value;
              setEditingItem({ ...editingItem, description: value });
              if (!value.trim()) {
                setDescriptionError("Description is required");
              } else if (value.length > 100) {
                setDescriptionError("Description must be less than 100 characters");
              } else {
                setDescriptionError("");
              }
            }}
            error={!!descriptionError}
            helperText={descriptionError}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              label="Type"
              value={editingItem?.type === "add-on" ? "add-on" : "fixed"}
              onChange={(e) =>
                setEditingItem({ ...editingItem, type: e.target.value })
              }
            >
              <MenuItem value="fixed">Fixed</MenuItem>
              <MenuItem value="add-on">Add-on</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="hasSubCategory-label">Has Subcategory</InputLabel>
            <Select
              labelId="hasSubCategory-label"
              label="Has Subcategory"
              value={editingItem?.hasSubCategory === 1 ? "Yes" : "No"}
              onChange={(e) =>
              {
                setEditingItem({ ...editingItem, hasSubCategory: e.target.value === "Yes" ? 1 : 0})
              }
              } 
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditingItem(null)}
            startIcon={<Close />}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!editingItem?.description || !editingItem.description.trim()) {
                setDescriptionError("Description is required");
                return;
              }
              if (editingItem.description.length > 100) {
                setDescriptionError("Description must be less than 100 characters");
                return;
              }
              handleSaveItem();
            }}
            variant="contained"
            startIcon={<Save />}
            className="save-btn"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

       {/* Dialog for adding subcategory */}
       <Dialog
        open={!!addSubcategoryItem}
        onClose={() => setAddSubcategoryItem(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {addSubcategoryItem?.id ? "Add Subcategory" : "Edit Subcategory"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={addSubcategoryItem?.description || ""}
            onChange={(e) => {
              const value = e.target.value;
              setAddSubcategoryItem({ ...addSubcategoryItem, description: value });
              if (!value.trim()) {
                setSubcategoryDescriptionError("Description is required");
              } else if (value.length > 100) {
                setSubcategoryDescriptionError("Description must be less than 100 characters");
              } else {
                setSubcategoryDescriptionError("");
              }
            }}
            error={!!subcategoryDescriptionError}
            helperText={subcategoryDescriptionError}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddSubcategoryItem(null)}
            startIcon={<Close />}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!addSubcategoryItem?.description || !addSubcategoryItem.description.trim()) {
                setSubcategoryDescriptionError("Description is required");
                return;
              }
              if (addSubcategoryItem.description.length > 100) {
                setSubcategoryDescriptionError("Description must be less than 100 characters");
                return;
              }
              handleSaveSubcategoryItem();
            }}
            variant="contained"
            startIcon={<Save />}
            className="save-btn"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for fee breakdown */}
      <Dialog
        open={!!feeBreakdownItem}
        onClose={handleCancelBreakdownFees}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Edit Fees -{" "}
          <span style={{ color: "var(--primary)" }}>
            {feeBreakdownItem?.category}
          </span>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Duration</TableCell>
                  <TableCell>Fees</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editedBreakdownFees
                  .sort((a, b) => {
                    const getMonths = (durationId) =>
                      feeData.durations.find((d) => d.id === durationId)
                        ?.monthsEquiv || 0;
                    return getMonths(a.durationId) - getMonths(b.durationId);
                  })
                  .map((fee, index) => (
                    <TableRow key={index}>
                      <TableCell>{fee.durationName}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={fee.amount}
                          onChange={(e) =>
                            handleEditBreakdownFee(index, e.target.value)
                          }
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleRestoreDefaultBreakdownFees}
            sx={{ color: "var(--primary-black)" }}
          >
            Restore
          </Button>
          <Button
            onClick={handleCancelBreakdownFees}
            startIcon={<Close />}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveBreakdownFees}
            variant="contained"
            startIcon={<Save />}
            className="save-btn"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle sx={{fontWeight:"bold"}}>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this fee? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} className="cancel-btn" startIcon={<Close />}>Cancel</Button>
          <Button
            color="error"
            onClick={async () => {
              await handleDeleteItem(agentToDelete);
              setDeleteConfirmOpen(false);
              setAgentToDelete(null);
            }}
            className="delete-btn"
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

AdminFeesUI.propTypes = {
  agentId: PropTypes.string,
};
